import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import UserManagement from './UserManagement';
import GoogleCalendarSettings from './GoogleCalendarSettings';
import BrandingSettings from './BrandingSettings';
import BillingSettings from './billing/BillingSettings';
import EmailTemplatesSettings from './EmailTemplatesSettings';
import SelfserviceSettings from './SelfserviceSettings';
import ProvidersPage from './ProvidersPage';
import TemplatesAdminPage from './TemplatesAdminPage';
import OperatoriesPage from './OperatoriesPage';
import ProceduresPage from './ProceduresPage';

type Tab =
  | 'email'
  | 'email-templates'
  | 'users'
  | 'gcal'
  | 'branding'
  | 'billing'
  | 'selfservice'
  | 'providers'
  | 'procedures'
  | 'note-templates'
  | 'operatories';

interface TabDef {
  id: Tab;
  labelKey: string;
  adminOnly?: boolean;
}

interface EmailSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  passSet: boolean;
}

const DEFAULTS: EmailSettings = {
  host: 'w014e07b.kasserver.com',
  port: 587,
  secure: false,
  user: 'info@seadent.es',
  passSet: false,
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState<Tab>('email');
  const [settings, setSettings] = useState<EmailSettings>(DEFAULTS);
  const [pass, setPass] = useState(''); // empty means "leave unchanged"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    let mounted = true;
    apiService.get<EmailSettings>('/settings/email')
      .then(resp => {
        if (mounted && resp.data?.success) {
          setSettings(resp.data.data);
        }
      })
      .catch(() => {
        if (mounted) addToast(t('settings.errorLoadEmail'), 'error');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [addToast, t]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: any = {
        host: settings.host,
        port: settings.port,
        secure: settings.secure,
        user: settings.user,
      };
      if (pass) body.pass = pass;
      const resp = await apiService.put<EmailSettings>('/settings/email', body);
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setPass('');
        addToast(t('settings.emailSaved'), 'success');
      } else {
        addToast(resp.data?.message || t('settings.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('settings.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) {
      addToast(t('settings.errorTestRecipient'), 'error');
      return;
    }
    setTesting(true);
    try {
      const resp = await apiService.post<any>('/settings/email/test', { to: testTo.trim() });
      if (resp.data?.success) {
        addToast(t('settings.testAccepted', { id: resp.data.data?.messageId || 'OK' }), 'success');
      } else {
        addToast(resp.data?.message || t('settings.errorTest'), 'error');
      }
    } catch (e: any) {
      const err = e?.response?.data;
      const detail = err?.error?.response || err?.message || t('settings.errorTest');
      addToast(t('settings.errorTestDetail', { detail }), 'error');
    } finally {
      setTesting(false);
    }
  };

  // Tab-Definitionen zentral, damit Mobile-Dropdown und Desktop-Tab-Leiste
  // dieselbe Liste teilen.
  const tabs: TabDef[] = useMemo(() => [
    { id: 'email', labelKey: 'settings.tabEmail' },
    { id: 'email-templates', labelKey: 'settings.tabEmailTemplates', adminOnly: true },
    { id: 'users', labelKey: 'settings.tabUsers', adminOnly: true },
    { id: 'gcal', labelKey: 'settings.tabGcal', adminOnly: true },
    { id: 'branding', labelKey: 'settings.tabBranding', adminOnly: true },
    { id: 'billing', labelKey: 'settings.tabBilling', adminOnly: true },
    { id: 'selfservice', labelKey: 'settings.tabSelfservice', adminOnly: true },
    { id: 'providers', labelKey: 'settings.tabProviders', adminOnly: true },
    { id: 'procedures', labelKey: 'settings.tabProcedures', adminOnly: true },
    { id: 'note-templates', labelKey: 'settings.tabNoteTemplates', adminOnly: true },
    { id: 'operatories', labelKey: 'settings.tabOperatories', adminOnly: true },
  ], []);

  const visibleTabs = tabs.filter(t => !t.adminOnly || isAdmin);

  if (loading && tab === 'email') {
    return <div className="p-6 text-gray-500">{t('settings.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-sm text-gray-600">{t('settings.subtitle')}</p>
      </div>

      {/* Mobile: Dropdown — bei vielen Tabs auf engen Screens UX-freundlicher
          als eine quetschende Tab-Leiste. */}
      <div className="sm:hidden">
        <label htmlFor="settings-tab-select" className="sr-only">{t('settings.selectAreaLabel')}</label>
        <select
          id="settings-tab-select"
          value={tab}
          onChange={(e) => setTab(e.target.value as Tab)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary outline-none"
        >
          {visibleTabs.map(tabDef => (
            <option key={tabDef.id} value={tabDef.id}>{t(tabDef.labelKey)}</option>
          ))}
        </select>
      </div>

      {/* Desktop: horizontale Tab-Leiste, scrollbar falls sie nicht ganz
          in den Container passt. */}
      <div className="hidden sm:block border-b border-gray-200">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label={t('settings.tabsAria')}>
          {visibleTabs.map(tabDef => (
            <button
              key={tabDef.id}
              onClick={() => setTab(tabDef.id)}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                tab === tabDef.id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t(tabDef.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'email-templates' && isAdmin && <EmailTemplatesSettings />}
      {tab === 'users' && isAdmin && <UserManagement />}
      {tab === 'gcal' && isAdmin && <GoogleCalendarSettings />}
      {tab === 'branding' && isAdmin && <BrandingSettings />}
      {tab === 'billing' && isAdmin && <BillingSettings />}
      {tab === 'selfservice' && isAdmin && <SelfserviceSettings />}
      {tab === 'providers' && isAdmin && <ProvidersPage />}
      {tab === 'procedures' && isAdmin && <ProceduresPage />}
      {tab === 'note-templates' && isAdmin && <TemplatesAdminPage />}
      {tab === 'operatories' && isAdmin && <OperatoriesPage />}

      {tab === 'email' && (
      <div className="max-w-3xl space-y-6">
      <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">✉️ {t('settings.smtpTitle')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            <Trans i18nKey="settings.smtpDescription" components={{ code: <code /> }} />
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('settings.host')}</label>
            <input
              type="text"
              value={settings.host}
              onChange={(e) => setSettings({ ...settings, host: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('settings.port')}</label>
            <input
              type="number"
              value={settings.port}
              onChange={(e) => setSettings({ ...settings, port: parseInt(e.target.value || '0', 10) })}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
            <p className="text-[11px] text-gray-500 mt-1">{t('settings.portHint')}</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('settings.user')}</label>
            <input
              type="text"
              value={settings.user}
              onChange={(e) => setSettings({ ...settings, user: e.target.value })}
              autoComplete="username"
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              {t('settings.password')} {settings.passSet && <span className="text-green-600 font-normal normal-case">{t('settings.passwordStored')}</span>}
            </label>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder={settings.passSet ? t('settings.passwordPlaceholderStored') : t('settings.passwordPlaceholderEnter')}
              autoComplete="new-password"
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div className="md:col-span-2 flex items-center space-x-2">
            <input
              id="secure"
              type="checkbox"
              checked={settings.secure}
              onChange={(e) => setSettings({ ...settings, secure: e.target.checked })}
              className="h-4 w-4"
            />
            <label htmlFor="secure" className="text-sm text-gray-700">
              <Trans i18nKey="settings.secureHint" components={{ code: <code /> }} />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-brand-primary text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {saving ? t('settings.saving') : t('common.save')}
          </button>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">🧪 {t('settings.testEmailTitle')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            <Trans i18nKey="settings.testEmailDescription" components={{ strong: <strong /> }} />
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder={t('settings.testRecipientPlaceholder')}
            className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
          />
          <button
            onClick={handleTest}
            disabled={testing || !testTo.trim()}
            className="px-5 py-2 bg-light-blue text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {testing ? t('settings.sending') : t('settings.sendTest')}
          </button>
        </div>
      </section>
      </div>
      )}
    </div>
  );
};

export default SettingsPage;
